import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Screen } from '../../ui';
import { useDraft } from '../../state/draft';
import { edited, useWhat } from '../../state/what';
import { APP_TYPES, TYPES, type AppType } from '../../journey/questions';
import { assignedVariant } from '../../analytics/experiment';
import { funnelStep, useFunnelStep, type FunnelProps } from '../../analytics/funnel';
import { readIntoDraft } from '../../journey/attach';
import { CREATE_ACCEPT } from './createPicks';
import { useThumbnails } from './thumbnails';

/**
 * /new/what — "What is it?" (V7, docs/superpowers/specs/2026-10-16-v7-build.md; V7-A/02-type-prompt).
 *
 * A toggle, Scheduler · Golf tracker · RSVP · Other, set by the ad they came from, and their sentence
 * under it — the ad's own, editable. Switching the type swaps in that type's example unless they have
 * edited the sentence, and the questions after this screen follow the type. The demo's button comes
 * here; so may anybody, who starts on Other with an empty field.
 */
export default function What() {
  const navigate = useNavigate();
  const type = useWhat((s) => s.type);
  const prompt = useWhat((s) => s.prompt);
  const filled = useWhat((s) => s.filled);
  // Not a member of the test (no ad): the short way, a, which is what most visitors see.
  const [group] = useState<'a' | 'b'>(() => assignedVariant('v7_flow') ?? 'a');
  useFunnelStep('what_viewed', true, { type });

  const attachedMedia = useDraft((s) => s.attachedMedia);
  const photos = useMemo(() => attachedMedia.filter((m) => m.contentType === 'image/jpeg'), [attachedMedia]);
  const thumbUrls = useThumbnails(photos);
  const thumbs = photos.map((p) => ({ name: p.name, url: thumbUrls[p.name] })).filter((t): t is { name: string; url: string } => Boolean(t.url));
  const [attaching, setAttaching] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  function choose(next: AppType): void {
    if (next === type) return;
    useWhat.getState().chooseType(next, group);
    funnelStep('what_type_changed', { type: next });
  }

  async function attach(files: File[]): Promise<void> {
    if (!files.length) return;
    setAttaching(true);
    setProblems([]);
    try {
      const { problems: found, addedPhotos, docs } = await readIntoDraft(files);
      setProblems(found);
      if (addedPhotos.length) funnelStep('composer_attach', { kind: 'photo', count: countBucket(addedPhotos.length) });
      if (docs) funnelStep('composer_attach', { kind: 'document', count: countBucket(docs) });
    } finally {
      setAttaching(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  function next(): void {
    const goal = prompt.trim();
    if (!goal) return;
    funnelStep('create_pressed', {
      origin: !filled.trim() ? 'typed' : edited({ prompt, filled }) ? 'prefilled_edited' : 'prefilled_unchanged',
      length: goal.length < 40 ? '<40' : goal.length < 120 ? '40-120' : goal.length < 300 ? '120-300' : '300+',
    });
    useWhat.getState().ensureAnswers(group);
    useWhat.getState().setStep(0);
    navigate('/new/questions');
  }

  const def = TYPES[type];
  return (
    <Screen>
      <div className="what">
        <h1 className="type-display what-title">What is it?</h1>
        <p className="type-body what-sub">Pick one, then edit the sentence.</p>
        <div className="what-toggle" role="radiogroup" aria-label="What is it?">
          {APP_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={t === type}
              className={t === type ? 'what-toggle-option is-on' : 'what-toggle-option'}
              onClick={() => choose(t)}
            >
              {TYPES[t].label}
            </button>
          ))}
        </div>
        <label className="type-caption what-label" htmlFor="what-prompt">Describe it</label>
        <div className="composer what-composer">
          {thumbs.length > 0 && (
            <ul className="composer-thumbs" aria-label="Attached photos">
              {thumbs.map((t) => <li key={t.name}><img src={t.url} alt="" /></li>)}
            </ul>
          )}
          <textarea
            id="what-prompt"
            className="field composer-input what-input"
            value={prompt}
            rows={5}
            placeholder={def.placeholder}
            onChange={(e) => useWhat.getState().setPrompt(e.target.value)}
          />
          <div className="composer-bar">
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={CREATE_ACCEPT}
              className="visually-hidden"
              aria-label="Photos or documents to add"
              onChange={(e) => void attach(Array.from(e.target.files ?? []))}
            />
            <button
              type="button"
              className="composer-plus"
              aria-label="Add photos or documents"
              disabled={attaching}
              onClick={() => fileInput.current?.click()}
            >
              +
            </button>
            <span className="type-caption composer-hint">
              {attaching ? 'Reading your files…' : attachedMedia.length ? `${attachedMedia.length} photo${attachedMedia.length === 1 ? '' : 's'} added` : 'Photos, a menu, a schedule'}
            </span>
          </div>
        </div>
        {problems.length > 0 && (
          <div role="alert" className="type-caption field-error-text">
            {problems.map((p) => <p key={p}>{p}</p>)}
          </div>
        )}
        <Button title="Next" onPress={next} disabled={!prompt.trim() || attaching} className="what-next" />
      </div>
    </Screen>
  );
}

function countBucket(n: number): NonNullable<FunnelProps['count']> {
  return n <= 1 ? '1' : n <= 5 ? '2-5' : '6+';
}
